import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { getProviderOrSigner } from 'utils'
import { CurrencyAmount, Token, Percent, JSBI, Currency, Trade } from '@magikswap/sdk'
import tryParseAmount from 'utils/tryParseAmount'
import { warningSeverity, formatExecutionPriceOdos, computeTradePriceBreakdown } from 'utils/exchange'
import {
  Button,
  ButtonMenu,
  ToggleMe,
  Text,
  ArrowDownIcon,
  Box,
  useModal,
  Flex,
  ArrowUpDownIcon,
  useMatchBreakpointsContext,
  useModalRefetch,
  AutoRenewIcon,
} from '@pancakeswap/uikit'
import { useIsTransactionUnsupported } from 'hooks/Trades'
import PropagateLoader from 'react-spinners/PropagateLoader'
import GridLoader from 'react-spinners/GridLoader'
import PacmanLoader from 'react-spinners/PacmanLoader'
import UnsupportedCurrencyFooter from 'components/UnsupportedCurrencyFooter'
import { useRouter } from 'next/router'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useTranslation } from '@pancakeswap/localization'
import { maxAmountSpend } from 'utils/maxAmountSpend'
import shouldShowSwapWarning from 'utils/shouldShowSwapWarning'
import { useSwapActionHandlers } from 'state/swap/useSwapActionHandlers'
import useRefreshBlockNumberID from './hooks/useRefreshBlockNumber'
import Column, { AutoColumn } from '../../components/Layout/Column'
import { AutoRow, RowBetween } from '../../components/Layout/Row'
import { SwapCallbackError, Wrapper } from './components/styleds'
import ProgressSteps from './components/ProgressSteps'
import ConnectWalletButton from '../../components/ConnectWalletButton'
import { useCurrency, useAllTokens } from '../../hooks/Tokens'
import {
  ApprovalState,
  useApproveCallbackFromTrade,
  useApproveCallbackFromTradeV3,
} from '../../hooks/useApproveCallback'
import useWrapCallback, { WrapType } from '../../hooks/useWrapCallback'
import { Field } from '../../state/swap/actions'
import {
  useDefaultsFromURLSearch,
  useSwapState,
  useSwapQuote,
  quoteAndAssemble,
  useDerivedSwapInfoOdos,
  useDerivedSwapInfo,
} from '../../state/swap/hooks'
import {
  useUserSlippageTolerance,
  useShowRoute,
  useUserSingleHopOnly,
  useExpertModeManager,
} from '../../state/user/hooks'
import CircleLoader from '../../components/Loader/CircleLoader'
import { BIPS_BASE, BIG_INT_ZERO, MULTI_SWAPS_SUPPORTED } from 'config/constants/exchange'
import Page from '../Page/Page'
import dynamic from 'next/dynamic'
import { SwapAndRoute, SwitchIconButton, StyledBalanceMaxMini, SwapBox, StyledSwapContainer } from './styles'
import Odos from './components/Odos'
import RouteView from './components/RouteView'
import { useSwapCallback } from 'hooks/useSwapCallback'
import confirmPriceImpactWithoutFee from './components/confirmPriceImpactWithoutFee'
import TradePrice from './components/TradePrice'
import { GreyCard } from '../../components/Card'

const CurrencyInputHeader = dynamic(() => import('./components/CurrencyInputHeader'), { ssr: true })
const SwapWarningModal = dynamic(() => import('./components/SwapWarningModal'), { ssr: false })
const ImportTokenWarningModal = dynamic(() => import('../../components/ImportTokenWarningModal'), { ssr: false })
const ConfirmSwapModal = dynamic(() => import('./components/ConfirmSwapModal'), { ssr: true })
const ConfirmSwapModalV2 = dynamic(() => import('../SwapClassic/components/ConfirmSwapModal'), { ssr: true })
const TWAP = dynamic(() => import('./components/TWAP').then((mod) => mod.TWAP), { ssr: false })
const CrossChainSwap = dynamic(() => import('./components/CrossChainSwap'), { ssr: false })
const SwapHeader = dynamic(() => import('./components/SwapHeader'), { ssr: true })
const TaxCard = dynamic(() => import('./components/TaxCard'), { ssr: false })
const CurrencyInputPanel = dynamic(() => import('../../components/CurrencyInputPanel'), { ssr: true })

export enum SwapType {
  REGULAR_SWAP,
  TWAP,
  LIMIT,
  CROSS_CHAIN_SWAP,
}
const Tabs = styled.div`
  width: 100%;
  margin-top: 1rem;
  @media (min-width: 1000px) {
    width: 50%;
  }
`

export default function Swap() {
  const router = useRouter()
  const loadedUrlParams = useDefaultsFromURLSearch()
  const { t } = useTranslation()
  const { isMobile, isDesktop } = useMatchBreakpointsContext()
  const [isChartExpanded, setIsChartExpanded] = useState(false)
  const { refreshBlockNumber, isLoading } = useRefreshBlockNumberID()
  const [showRoute] = useShowRoute()
  const [assembledTransaction, setAssembledTransaction] = useState<any>(null)
  const [quoteData, setQuoteData] = useState<any>(null)
  const [isV2, setIsV2] = useState<boolean>(false)
  const [loadingConfirm, setLoadingConfirm] = useState(false)
  const { account, chainId, library } = useActiveWeb3React()

  const [view, setView] = useState(SwapType.REGULAR_SWAP)
  const handleClick = (newIndex: number) => {
    setView(newIndex)
  }

  const TabsComponent: React.FC = () => {
    return (
      <>
        {MULTI_SWAPS_SUPPORTED.includes(chainId) ? (
          <Flex
            flexDirection="column"
            backgroundColor=""
            width="100%"
            mb="1rem"
            justifyContent="center"
            alignItems="center"
          >
            <Tabs>
              <ButtonMenu scale="swaptoggle" onItemClick={handleClick} activeIndex={view} fullWidth>
                <ToggleMe style={{ height: 40 }}>{t('BaseSwap')}</ToggleMe>
                <ToggleMe style={{ height: 40 }}>{t('TWAP')}</ToggleMe>
                <ToggleMe style={{ height: 40 }}>{t('Limit')}</ToggleMe>
                <ToggleMe style={{ height: 40, whiteSpace: 'nowrap' }}>{t('Cross-Chain')}</ToggleMe>
              </ButtonMenu>
            </Tabs>
          </Flex>
        ) : (
          <></>
        )}
      </>
    )
  }

  const isChartDisplayed = false
  const setIsChartDisplayed = null

  // for expert mode
  const [isExpertMode] = useExpertModeManager()

  // token warning stuff
  const [loadedInputCurrency, loadedOutputCurrency] = [
    useCurrency(loadedUrlParams?.inputCurrencyId),
    useCurrency(loadedUrlParams?.outputCurrencyId),
  ]
  const urlLoadedTokens: Token[] = useMemo(
    () => [loadedInputCurrency, loadedOutputCurrency]?.filter((c): c is Token => c instanceof Token) ?? [],
    [loadedInputCurrency, loadedOutputCurrency],
  )

  // dismiss warning if all imported tokens are in active lists
  const defaultTokens = useAllTokens()

  const importTokensNotInDefault =
    urlLoadedTokens &&
    urlLoadedTokens.filter((token: Token) => {
      return !(token.address in defaultTokens)
    })

  // get custom setting values for user
  const [allowedSlippage] = useUserSlippageTolerance()

  // swap state & price data
  const {
    independentField,
    typedValue,
    recipient,
    [Field.INPUT]: { currencyId: inputCurrencyId },
    [Field.OUTPUT]: { currencyId: outputCurrencyId },
  } = useSwapState()

  const inputCurrency = useCurrency(inputCurrencyId)
  const outputCurrency = useCurrency(outputCurrencyId)

  useEffect(() => {
    if (!inputCurrency || !outputCurrency) return
    const specialTokens = [
      '0xFF0C532FDB8Cd566Ae169C1CB157ff2Bdc83E105',
      '0xF6e932Ca12afa26665dC4dDE7e27be02A7c02e50',
      '0xAC1Bd2486aAf3B5C0fc3Fd868558b082a531B2B4',
      '0x3eAf5c582e3bBABa160662A7cC43f08F26c6c225',
    ]

    // @ts-ignore
    const shouldBeV2 = specialTokens.includes(inputCurrency.address) || specialTokens.includes(outputCurrency.address)
    setIsV2(shouldBeV2)
  }, [inputCurrency, outputCurrency])

  const {
    v2Trade,
    currencyBalances: currencyBalancesV2,
    parsedAmount: parsedAmountV2,
    currencies: currenciesV2,
    inputError: swapInputErrorV2,
  } = useDerivedSwapInfo(independentField, typedValue, inputCurrency, outputCurrency, recipient)

  const {
    currencyBalances,
    parsedAmount,
    currencies,
    inputError: swapInputError,
  } = useDerivedSwapInfoOdos(independentField, typedValue, inputCurrency, outputCurrency, recipient)

  const swapData = useSwapQuote(inputCurrency, outputCurrency, typedValue, allowedSlippage)

  const {
    wrapType,
    execute: onWrap,
    inputError: wrapInputError,
  } = useWrapCallback(currencies[Field.INPUT], currencies[Field.OUTPUT], typedValue)

  const showWrap: boolean = wrapType !== WrapType.NOT_APPLICABLE
  const trade = showWrap ? undefined : v2Trade
  // const singleTokenPrice = useSingleTokenSwapInfo(inputCurrencyId, inputCurrency, outputCurrencyId, outputCurrency)
  const inputAmount = parseInt(swapData?.data?.inAmounts[0]) / 10 ** inputCurrency?.decimals
  const outputAmount = parseInt(swapData?.data?.outAmounts[0]) / 10 ** outputCurrency?.decimals
  const parsedAmounts = showWrap
    ? {
        [Field.INPUT]: parsedAmount,
        [Field.OUTPUT]: parsedAmount,
      }
    : {
        [Field.INPUT]: parsedAmount,
        [Field.OUTPUT]: tryParseAmount(outputAmount.toString(), outputCurrency),
      }

  const parsedAmountsV2 = showWrap
    ? {
        [Field.INPUT]: parsedAmount,
        [Field.OUTPUT]: parsedAmount,
      }
    : {
        [Field.INPUT]: independentField === Field.INPUT ? parsedAmount : trade?.inputAmount,
        [Field.OUTPUT]: independentField === Field.OUTPUT ? parsedAmount : trade?.outputAmount,
      }

  const { onSwitchTokens, onCurrencySelection, onUserInput, onChangeRecipient } = useSwapActionHandlers()
  const isValid = !swapInputError
  const isValidV2 = !swapInputErrorV2
  const dependentField: Field = independentField === Field.INPUT ? Field.OUTPUT : Field.INPUT
  const handleTypeInput = useCallback(
    (value: string) => {
      onUserInput(Field.INPUT, value)
    },
    [onUserInput],
  )
  const handleTypeOutput = useCallback(
    (value: string) => {
      onUserInput(Field.OUTPUT, value)
    },
    [onUserInput],
  )

  // modal and loading
  const [{ tradeToConfirmV2, swapErrorMessageV2, attemptingTxnV2, txHashV2 }, setSwapStateV2] = useState<{
    tradeToConfirmV2: Trade | undefined
    attemptingTxnV2: boolean
    swapErrorMessageV2: string | undefined
    txHashV2: string | undefined
  }>({
    tradeToConfirmV2: undefined,
    attemptingTxnV2: false,
    swapErrorMessageV2: undefined,
    txHashV2: undefined,
  })

  // modal and loading
  const [{ swapErrorMessage, attemptingTxn, txHash }, setSwapState] = useState<{
    attemptingTxn: boolean
    swapErrorMessage: string | undefined
    txHash: string | undefined
  }>({
    attemptingTxn: false,
    swapErrorMessage: undefined,
    txHash: undefined,
  })

  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: showWrap
      ? typedValue ?? ''
      : tryParseAmount(outputAmount.toString(), outputCurrency)?.toSignificant(6) ?? '',
  }

  const formattedAmountsV2 = {
    [independentField]: typedValue,
    [dependentField]: showWrap
      ? parsedAmountsV2[independentField]?.toExact() ?? ''
      : parsedAmountsV2[dependentField]?.toSignificant(6) ?? '',
  }

  const route = trade?.route
  const userHasSpecifiedInputOutput = Boolean(
    currencies[Field.INPUT] && currencies[Field.OUTPUT] && parsedAmounts[independentField]?.greaterThan(BIG_INT_ZERO),
  )
  const userHasSpecifiedInputOutputV2 = Boolean(
    currencies[Field.INPUT] && currencies[Field.OUTPUT] && parsedAmountsV2[independentField]?.greaterThan(BIG_INT_ZERO),
  )
  const noRoute = !route

  // check whether the user has approved the router on the input token
  const [approvalV2, approveCallbackV2] = useApproveCallbackFromTrade(trade, allowedSlippage, chainId)

  // check whether the user has approved the router on the input token
  // @ts-ignore
  const [approval, approveCallback] = useApproveCallbackFromTradeV3(inputCurrency, typedValue, allowedSlippage, chainId)

  // check if user has gone through approval process, used to show two step buttons, reset on token change
  const [approvalSubmitted, setApprovalSubmitted] = useState<boolean>(false)
  const [approvalSubmittedV2, setApprovalSubmittedV2] = useState<boolean>(false)
  // mark when a user has submitted an approval, reset onTokenSelection for input field
  useEffect(() => {
    if (approval === ApprovalState.PENDING) {
      setApprovalSubmitted(true)
    }
  }, [approval, approvalSubmitted])
  useEffect(() => {
    if (approvalV2 === ApprovalState.PENDING) {
      setApprovalSubmittedV2(true)
    }
  }, [approvalV2, approvalSubmittedV2])

  const sendTransaction = async (assembledData) => {
    const signer = getProviderOrSigner(library, account)
    try {
      setSwapState({ attemptingTxn: true, swapErrorMessage: undefined, txHash: undefined })
      //@ts-ignore
      const transactionResponse = await signer.sendTransaction({
        to: assembledData.transaction.to,
        data: assembledData.transaction.data,
        gasLimit: assembledData.transaction.gas === -1 ? 200000 : assembledData.transaction.gas,
        gasPrice: assembledData.transaction.gasPrice,
        nonce: assembledData.transaction.nonce,
        value: assembledData.transaction.value,
      })
      setSwapState({ attemptingTxn: false, swapErrorMessage: undefined, txHash: transactionResponse.hash })
    } catch (error) {
      //@ts-ignore

      setSwapState({ attemptingTxn: false, swapErrorMessage: error.message, txHash: undefined })
    }
  }

  const maxAmountInput: CurrencyAmount | undefined = maxAmountSpend(currencyBalances[Field.INPUT])
  const atMaxAmountInput = Boolean(maxAmountInput && parsedAmounts[Field.INPUT]?.equalTo(maxAmountInput))
  // const atMaxAmountInputV2 = Boolean(maxAmountInput && parsedAmountsV2[Field.INPUT]?.equalTo(maxAmountInput))

  // the callback to execute the swap
  const { callback: swapCallback, error: swapCallbackError } = useSwapCallback(trade, allowedSlippage, recipient)

  const priceImpactSD = swapData?.data && (-100 * swapData.data.priceImpact).toFixed(0)
  const priceImpactWithoutFee = priceImpactSD && new Percent(JSBI.BigInt(priceImpactSD), BIPS_BASE)

  const { priceImpactWithoutFee: priceImpactWithoutFeeV2 } = computeTradePriceBreakdown(trade)

  const [singleHopOnly] = useUserSingleHopOnly()

  const handleSwap = useCallback(() => {
    if (priceImpactWithoutFeeV2 && !confirmPriceImpactWithoutFee(priceImpactWithoutFeeV2, t)) {
      return
    }

    if (!swapCallback) {
      return
    }

    setSwapStateV2({ attemptingTxnV2: true, tradeToConfirmV2, swapErrorMessageV2: undefined, txHashV2: undefined })

    swapCallback()
      .then((hash) => {
        setSwapStateV2({ attemptingTxnV2: false, tradeToConfirmV2, swapErrorMessageV2: undefined, txHashV2: hash })
      })
      .catch((error) => {
        console.log('swap error', error.message)
        setSwapStateV2({
          attemptingTxnV2: false,
          tradeToConfirmV2,
          swapErrorMessageV2: error.message,
          txHashV2: undefined,
        })
      })
  }, [priceImpactWithoutFeeV2, swapCallback, tradeToConfirmV2, t])

  // errors
  const [showInverted, setShowInverted] = useState<boolean>(false)

  // warnings on slippage
  const priceImpactSeverity = warningSeverity(priceImpactWithoutFee)
  const priceImpactSeverityV2 = warningSeverity(priceImpactWithoutFeeV2)

  // show approve flow when: no error on inputs, not approved or pending, or approved in current session
  // never show if price impact is above threshold in non expert mode
  const showApproveFlow =
    !swapInputError &&
    (approval === ApprovalState.NOT_APPROVED ||
      approval === ApprovalState.PENDING ||
      (approvalSubmitted && approval === ApprovalState.APPROVED)) &&
    !(priceImpactSeverity > 3)

  const showApproveFlowV2 =
    !swapInputErrorV2 &&
    (approvalV2 === ApprovalState.NOT_APPROVED ||
      approvalV2 === ApprovalState.PENDING ||
      (approvalSubmittedV2 && approvalV2 === ApprovalState.APPROVED)) &&
    !(priceImpactSeverityV2 > 3 && !isExpertMode)

  const handleConfirmDismiss = useCallback(() => {
    setSwapState({ attemptingTxn, swapErrorMessage, txHash })
    // if there was a tx hash, we want to clear the input
    if (txHash) {
      onUserInput(Field.INPUT, '')
    }
  }, [attemptingTxn, onUserInput, swapErrorMessage, txHash])

  const handleConfirmDismissV2 = useCallback(() => {
    setSwapStateV2({ tradeToConfirmV2, attemptingTxnV2, swapErrorMessageV2, txHashV2 })
    // if there was a tx hash, we want to clear the input
    if (txHash) {
      onUserInput(Field.INPUT, '')
    }
  }, [attemptingTxnV2, onUserInput, swapErrorMessageV2, tradeToConfirmV2, txHashV2])

  const handleAcceptChangesV2 = useCallback(() => {
    setSwapStateV2({ tradeToConfirmV2: trade, swapErrorMessageV2, txHashV2, attemptingTxnV2 })
  }, [attemptingTxnV2, swapErrorMessageV2, trade, txHashV2])

  // swap warning state
  const [swapWarningCurrency, setSwapWarningCurrency] = useState(null)
  const [onPresentSwapWarningModal] = useModal(<SwapWarningModal swapCurrency={swapWarningCurrency} />, false)

  useEffect(() => {
    if (swapWarningCurrency) {
      onPresentSwapWarningModal()
    }
  }, [swapWarningCurrency])

  const handleInputSelect = useCallback(
    (currencyInput) => {
      setApprovalSubmitted(false) // reset 2 step UI for approvals
      setApprovalSubmittedV2(false) // reset 2 step UI for approvals
      onCurrencySelection(Field.INPUT, currencyInput)
      const showSwapWarning = shouldShowSwapWarning(currencyInput)
      if (showSwapWarning) {
        setSwapWarningCurrency(currencyInput)
      } else {
        setSwapWarningCurrency(null)
      }
    },
    [onCurrencySelection],
  )

  const handleMaxInput = useCallback(() => {
    if (maxAmountInput) {
      onUserInput(Field.INPUT, maxAmountInput.toExact())
    }
  }, [maxAmountInput, onUserInput])

  const handleOutputSelect = useCallback(
    (currencyOutput) => {
      onCurrencySelection(Field.OUTPUT, currencyOutput)
      const showSwapWarning = shouldShowSwapWarning(currencyOutput)
      if (showSwapWarning) {
        setSwapWarningCurrency(currencyOutput)
      } else {
        setSwapWarningCurrency(null)
      }
    },

    [onCurrencySelection],
  )

  const swapIsUnsupported = useIsTransactionUnsupported(currencies?.INPUT, currencies?.OUTPUT)

  const [onPresentImportTokenWarningModal] = useModal(
    <ImportTokenWarningModal tokens={importTokensNotInDefault} onCancel={() => router.push('/swap')} />,
  )

  useEffect(() => {
    if (importTokensNotInDefault.length > 0) {
      onPresentImportTokenWarningModal()
    }
  }, [importTokensNotInDefault.length])

  const fetchCallback = useCallback(async () => {
    const { quoteData, assembledData } = await quoteAndAssemble(
      inputCurrency,
      outputCurrency,
      typedValue,
      allowedSlippage,
      account,
    )
    setAssembledTransaction(assembledData)
    setQuoteData(quoteData)
  }, [inputCurrency, outputCurrency, typedValue, allowedSlippage, account])

  const [onPresentConfirmModal] = useModalRefetch(
    <ConfirmSwapModal
      attemptingTxn={attemptingTxn}
      txHash={txHash}
      recipient={recipient}
      allowedSlippage={allowedSlippage}
      //@ts-ignore
      onConfirm={sendTransaction}
      swapErrorMessage={swapErrorMessage}
      customOnDismiss={handleConfirmDismiss}
      assembledTransaction={assembledTransaction}
      swapData={quoteData}
      inputCurrency={inputCurrency}
      outputCurrency={outputCurrency}
      formattedAmounts={formattedAmounts}
      loadingConfirm={loadingConfirm}
    />,
    true,
    true,
    'confirmSwapModal',
    fetchCallback,
  )

  const [onPresentConfirmModalV2] = useModal(
    <ConfirmSwapModalV2
      trade={trade}
      originalTrade={tradeToConfirmV2}
      onAcceptChanges={handleAcceptChangesV2}
      attemptingTxn={attemptingTxnV2}
      txHash={txHashV2}
      recipient={recipient}
      allowedSlippage={allowedSlippage}
      onConfirm={handleSwap}
      swapErrorMessage={swapErrorMessageV2}
      customOnDismiss={handleConfirmDismissV2}
    />,
    true,
    true,
    'confirmSwapModalV2',
  )

  // Use a ref to track whether the component has mounted
  const isMounted = useRef(false)

  useEffect(() => {
    if (isMounted.current && inputCurrency && outputCurrency && typedValue && allowedSlippage && account) {
      fetchCallback() // Call the fetchCallback when 'account' changes
    } else {
      isMounted.current = true
    }
  }, [account])

  // trigger useEffect on typed value change, et al
  const hasAmount = Boolean(parsedAmount)
  const onRefreshPrice = useCallback(() => {
    if (hasAmount) {
      refreshBlockNumber()
    }
  }, [hasAmount, refreshBlockNumber])

  return (
    <Page
      backgroundImageSrc="/images/vikings/clean/standingviking.webp"
      backgroundDesktopOpacity={0.2}
      backgroundDefaultOpacity={0.25}
    >
      <SwapHeader />

      {view === SwapType.CROSS_CHAIN_SWAP && (
        <Flex width="100%" justifyContent="center" position="relative" marginBottom="100px">
          <CrossChainSwap />
        </Flex>
      )}

      {view === SwapType.REGULAR_SWAP && (
        <Flex
          width="100%"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          position="relative"
          marginLeft="auto"
          marginRight="auto"
        >
          <SwapAndRoute>
            {/* <StyledSwapContainer $isChartExpanded={isChartExpanded} style={{ margin: !showRoute || isV2 ? 'auto' : 0, width: !showRoute || isV2 ? '100%' : 'auto' }} > */}
            <SwapBox mt={isChartExpanded ? '24px' : '12px'} mx={isChartExpanded ? '24px' : '0px'}>
              {/* <RealAppBodySwap marginTop="0rem"> */}
              <Flex flexDirection="column" padding="0px" marginTop="12px">
                <CurrencyInputPanel
                  label={independentField === Field.OUTPUT && !showWrap ? t('From (estimated)') : t('From')}
                  value={isV2 ? formattedAmountsV2[Field.INPUT] : formattedAmounts[Field.INPUT]}
                  showMaxButton
                  currency={currencies[Field.INPUT]}
                  onUserInput={handleTypeInput}
                  onMax={handleMaxInput}
                  onCurrencySelect={handleInputSelect}
                  otherCurrency={currencies[Field.OUTPUT]}
                  showBUSD
                  id="swap-currency-input"
                />

                <Flex flexDirection="row" justifyContent="center" marginY="-18px" zIndex="1">
                  <SwitchIconButton
                    variant="gasandslippage"
                    onClick={() => {
                      setApprovalSubmitted(false) // reset 2 step UI for approvals
                      setApprovalSubmittedV2(false) // reset 2 step UI for approvals
                      onSwitchTokens()
                    }}
                  >
                    <ArrowDownIcon
                      className="icon-down"
                      color={currencies[Field.INPUT] && currencies[Field.OUTPUT] ? 'text' : 'text'}
                    />
                    <ArrowUpDownIcon
                      className="icon-up-down"
                      color={currencies[Field.INPUT] && currencies[Field.OUTPUT] ? 'text' : 'text'}
                    />
                  </SwitchIconButton>
                </Flex>

                <CurrencyInputPanel
                  value={isV2 ? formattedAmountsV2[Field.OUTPUT] : formattedAmounts[Field.OUTPUT]}
                  onUserInput={handleTypeOutput}
                  label={independentField === Field.INPUT && !showWrap && swapData ? t('To (estimated)') : t('To')}
                  showMaxButton={false}
                  currency={currencies[Field.OUTPUT]}
                  onCurrencySelect={handleOutputSelect}
                  loading={swapData.isLoading}
                  otherCurrency={currencies[Field.INPUT]}
                  id="swap-currency-output"
                  showBUSD
                  disabled
                />

                {showWrap && !isV2 ? null : (
                  <AutoColumn gap="7px" style={{ padding: '0 16px' }}>
                    <Flex
                      marginTop="8px"
                      flexDirection="row"
                      width="100%"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      {swapData.data && (
                        <>
                          <Text fontSize="12px" color="#fff" textTransform="uppercase">
                            Rate: &nbsp;
                            {inputAmount &&
                              outputAmount &&
                              inputCurrency &&
                              outputCurrency &&
                              formatExecutionPriceOdos(
                                inputAmount,
                                outputAmount,
                                inputCurrency,
                                outputCurrency,
                                showInverted,
                              )}
                          </Text>
                          <StyledBalanceMaxMini onClick={() => setShowInverted(!showInverted)}>
                            <AutoRenewIcon width="12px" />
                          </StyledBalanceMaxMini>

                          {/* <Text fontSize="12px" color="#fff" textTransform="uppercase">
                            Gas Price: ${swapData.data.gasEstimateValue.toFixed(2)}{' '}
                          </Text> */}
                        </>
                      )}
                    </Flex>
                    <TaxCard inputCurrency={inputCurrency} outputCurrency={outputCurrency}></TaxCard>
                  </AutoColumn>
                )}
              </Flex>
              {!isV2 && (
                <>
                  <Box mt="0.25rem" padding="4px">
                    {swapIsUnsupported ? (
                      <Button variant="secondarytrans" width="100%" disabled>
                        <Text color="#fff" fontSize="14px">
                          {' '}
                          That's a scam. You're welcome.{' '}
                        </Text>
                      </Button>
                    ) : !account ? (
                      <ConnectWalletButton width="100%" />
                    ) : showWrap ? (
                      <Button variant="secondarytrans" width="100%" disabled={Boolean(wrapInputError)} onClick={onWrap}>
                        {wrapInputError ??
                          (wrapType === WrapType.WRAP ? 'Wrap' : wrapType === WrapType.UNWRAP ? 'Unwrap' : null)}
                      </Button>
                    ) : !swapData && userHasSpecifiedInputOutput ? (
                      // noRoute && userHasSpecifiedInputOutput ? (

                      <Text fontSize="14px" color="textSubtle">
                        {t('Insufficient liquidity for this trade.')}
                      </Text>
                    ) : showApproveFlow ? (
                      <RowBetween>
                        <Button
                          variant={approval === ApprovalState.APPROVED ? 'secondarytrans' : 'primary'}
                          onClick={approveCallback}
                          disabled={approval !== ApprovalState.NOT_APPROVED || approvalSubmitted}
                          width="48%"
                        >
                          {approval === ApprovalState.PENDING ? (
                            <AutoRow gap="6px" justify="center">
                              {t('Enabling')} <CircleLoader stroke="white" />
                            </AutoRow>
                          ) : approvalSubmitted && approval === ApprovalState.APPROVED ? (
                            t('Enabled')
                          ) : (
                            t('Enable %asset%', { asset: currencies[Field.INPUT]?.symbol ?? '' })
                          )}
                        </Button>
                        <Button
                          variant={isValid && priceImpactSeverity > 2 ? 'danger' : 'primary'}
                          onClick={async () => {
                            // Set initial state
                            setSwapState({
                              attemptingTxn: false,
                              swapErrorMessage: undefined,
                              txHash: undefined,
                            })
                            setLoadingConfirm(true)

                            // Prepare transaction data
                            const { quoteData, assembledData } = await quoteAndAssemble(
                              inputCurrency,
                              outputCurrency,
                              typedValue,
                              allowedSlippage,
                              account,
                            )
                            setAssembledTransaction(assembledData)
                            setQuoteData(quoteData)

                            // Trigger the transaction
                            sendTransaction(assembledData)

                            setLoadingConfirm(false)
                          }}
                          width="48%"
                          id="swap-button"
                          disabled={!isValid || approval !== ApprovalState.APPROVED || priceImpactSeverity > 3}
                        >
                          {priceImpactSeverity > 3
                            ? t('Price Impact High')
                            : priceImpactSeverity > 2
                            ? t('Swap Anyway')
                            : t('Swap')}
                        </Button>
                      </RowBetween>
                    ) : (
                      <Button
                        variant={isValid && priceImpactSeverity > 2 ? 'secondarytrans' : 'primary'}
                        onClick={async () => {
                          setLoadingConfirm(true)
                          setSwapState({
                            attemptingTxn: false,
                            swapErrorMessage: undefined,
                            txHash: undefined,
                          })
                          const { quoteData, assembledData } = await quoteAndAssemble(
                            inputCurrency,
                            outputCurrency,
                            typedValue,
                            allowedSlippage,
                            account,
                          )
                          setAssembledTransaction(assembledData)
                          setQuoteData(quoteData)
                          await sendTransaction(assembledData)

                          setLoadingConfirm(false)
                        }}
                        id="swap-button"
                        width="100%"
                        disabled={!isValid || priceImpactSeverity > 3}
                      >
                        {loadingConfirm ? (
                          <Flex flexDirection="row" justifyContent="flex-start" alignItems="center">
                            <PacmanLoader
                              color="#fff"
                              // loading={loading}
                              // cssOverride={override}
                              size={10}
                              // speedMultiplier={0.5}
                              aria-label="Loading Spinner"
                              data-testid="loader"
                            />

                            <Text marginLeft="2rem">Confirm in Wallet </Text>
                          </Flex>
                        ) : (
                          swapInputError ||
                          (priceImpactSeverity > 3 ? (
                            t('Price Impact Too High')
                          ) : priceImpactSeverity > 2 ? (
                            t('Swap Anyway')
                          ) : (
                            <Flex flexDirection="row" justifyContent="center" alignItems="center">
                              <GridLoader
                                color="#fff"
                                // loading={loading}
                                // cssOverride={override}
                                size={3}
                                // speedMultiplier={0.5}
                                aria-label="Loading Spinner"
                                data-testid="loader"
                              />

                              <Text marginLeft="8px">Confirm Swap </Text>
                            </Flex>
                          ))
                        )}
                      </Button>
                    )}
                    {showApproveFlow && (
                      <Column style={{ marginTop: '1rem' }}>
                        <ProgressSteps steps={[approval === ApprovalState.APPROVED]} />
                      </Column>
                    )}
                    {/* {swapErrorMessage ? <SwapCallbackError error={swapErrorMessage} /> : null} */}
                  </Box>

                  {/* <Odos /> */}
                </>
              )}
              {isV2 && (
                <Box mt="0.25rem" mb="1rem">
                  {swapIsUnsupported ? (
                    <Button style={{ backgroundColor: '#333', borderWidth: '2px' }} width="100%" disabled>
                      <Text color="#fff"> NOPE. That's a scam. You're welcome. </Text>
                    </Button>
                  ) : !account ? (
                    <ConnectWalletButton width="100%" />
                  ) : showWrap ? (
                    <Button width="100%" disabled={Boolean(wrapInputError)} onClick={onWrap}>
                      {wrapInputError ??
                        (wrapType === WrapType.WRAP ? 'Wrap' : wrapType === WrapType.UNWRAP ? 'Unwrap' : null)}
                    </Button>
                  ) : noRoute && userHasSpecifiedInputOutput ? (
                    <GreyCard style={{ textAlign: 'center', padding: '0.75rem' }}>
                      <Text color="textSubtle">{t('Insufficient liquidity for this trade.')}</Text>
                      {singleHopOnly && <Text color="textSubtle">{t('Try enabling multi-hop trades.')}</Text>}
                    </GreyCard>
                  ) : showApproveFlowV2 ? (
                    <RowBetween>
                      <Button
                        variant={approvalV2 === ApprovalState.APPROVED ? 'success' : 'primary'}
                        onClick={approveCallbackV2}
                        disabled={approvalV2 !== ApprovalState.NOT_APPROVED || approvalSubmittedV2}
                        width="48%"
                      >
                        {approvalV2 === ApprovalState.PENDING ? (
                          <AutoRow gap="6px" justify="center">
                            {t('Enabling')} <CircleLoader stroke="white" />
                          </AutoRow>
                        ) : approvalSubmittedV2 && approvalV2 === ApprovalState.APPROVED ? (
                          t('ENABLE')
                        ) : (
                          t('ENABLE %asset%', { asset: currencies[Field.INPUT]?.symbol ?? '' })
                        )}
                      </Button>
                      <Button
                        variant={isValidV2 && priceImpactSeverityV2 > 2 ? 'danger' : 'primary'}
                        onClick={() => {
                          if (isExpertMode) {
                            handleSwap()
                          } else {
                            setSwapStateV2({
                              tradeToConfirmV2: trade,
                              attemptingTxnV2: false,
                              swapErrorMessageV2: undefined,
                              txHashV2: undefined,
                            })
                            onPresentConfirmModalV2()
                          }
                        }}
                        width="48%"
                        id="swap-button"
                        disabled={
                          !isValidV2 ||
                          approvalV2 !== ApprovalState.APPROVED ||
                          (priceImpactSeverityV2 > 3 && !isExpertMode)
                        }
                      >
                        {priceImpactSeverityV2 > 3 && !isExpertMode
                          ? t('Price Impact High')
                          : priceImpactSeverityV2 > 2
                          ? t('Swap Anyway')
                          : t('Swap')}
                      </Button>
                    </RowBetween>
                  ) : (
                    <Button
                      variant={isValidV2 && priceImpactSeverityV2 > 2 && !swapCallbackError ? 'danger' : 'primary'}
                      onClick={() => {
                        if (isExpertMode) {
                          handleSwap()
                        } else {
                          setSwapStateV2({
                            tradeToConfirmV2: trade,
                            attemptingTxnV2: false,
                            swapErrorMessageV2: undefined,
                            txHashV2: undefined,
                          })
                          onPresentConfirmModalV2()
                        }
                      }}
                      id="swap-button"
                      width="100%"
                      disabled={!isValidV2 || (priceImpactSeverityV2 > 3 && !isExpertMode) || !!swapCallbackError}
                    >
                      {swapInputErrorV2 ||
                        (priceImpactSeverityV2 > 3 && !isExpertMode
                          ? t('Price Impact Too High')
                          : priceImpactSeverityV2 > 2
                          ? t('Swap Anyway')
                          : t('Swap'))}
                    </Button>
                  )}
                  {showApproveFlowV2 && (
                    <Column style={{ marginTop: '1rem' }}>
                      <ProgressSteps steps={[approvalV2 === ApprovalState.APPROVED]} />
                    </Column>
                  )}
                  {isExpertMode && swapErrorMessageV2 ? <SwapCallbackError error={swapErrorMessageV2} /> : null}
                </Box>
              )}

              <CurrencyInputHeader
                title={t('')}
                subtitle={t('')}
                setIsChartDisplayed={setIsChartDisplayed}
                isChartDisplayed={isChartDisplayed}
                hasAmount={hasAmount}
                onRefreshPrice={onRefreshPrice}
                isV2={isV2}
              />
              {/* </RealAppBodySwap> */}

              {/* </Casing> */}
              {/* CAN ADD THIS BACK LATER */}
              {/* {!swapIsUnsupported ? (
          trade && <AdvancedSwapDetailsDropdown trade={trade} />
        ) : ( */}
              {swapIsUnsupported && (
                <UnsupportedCurrencyFooter
                  currencies={[currencies.INPUT, currencies.OUTPUT]}
                  show={swapIsUnsupported}
                />
              )}
            </SwapBox>
            {/* </StyledSwapContainer> */}
            {!isV2 && <RouteView />}
          </SwapAndRoute>
          <Odos />
        </Flex>
      )}
      {view === SwapType.TWAP && <TWAP isChartExpanded={isChartExpanded} />}
      {view === SwapType.LIMIT && <TWAP limit isChartExpanded={isChartExpanded} />}
      <TabsComponent />
    </Page>
  )
}
